import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { initializeProfessionsCacheNew, getProfessionOptionsForSearch } from '@/services/professions/getProfessionsNew';
import { getCities, loadCitiesFromDB, israelRegions } from '@/utils/locationData';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import { getLabel, fuzzyMatch } from '@/utils/textUtils';

interface SearchBarProps {
  onSearch: (profession: string, location: string, displayName?: string) => void;
  useCities?: boolean;
  initialProfession?: string;
  initialLocation?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  useCities = false,
  initialProfession = '',
  initialLocation = ''
}) => {
  const { t, i18n } = useTranslation();
  const { direction } = useDirection();
  const currentLang = i18n.language;
  const [profession, setProfession] = useState(initialProfession || 'all');
  const [location, setLocation] = useState(initialLocation || 'all');
  const [isOpen, setIsOpen] = useState({ profession: false, location: false });
  const [searchText, setSearchText] = useState({ profession: '', location: '' });
  const [professionError, setProfessionError] = useState<string | null>(null);
  const [professionOptions, setProfessionOptions] = useState<Array<{
    id: string;
    label: string;
    label_en?: string;
    label_fr?: string;
    label_ru?: string;
    type?: 'main' | 'sub' | 'all';
    mainProfessionId?: string;
    searchTerms?: string[];
  }>>([]);
  const [cities, setCities] = useState<Array<{
    id: string;
    label: string;
    label_en?: string;
    label_fr?: string;
    label_ru?: string;
  }>>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load cities from database when useCities is true
  useEffect(() => {
    const loadCities = async () => {
      await loadCitiesFromDB();
      setCities(getCities());
    };
    if (useCities) {
      loadCities();
    }
  }, [useCities]);

  const locationOptions = useCities ? cities : israelRegions;

  // Load profession options from database (new UUID-based system)
  useEffect(() => {
    const loadProfessions = async () => {
      await initializeProfessionsCacheNew();
      const options = getProfessionOptionsForSearch();
      // Enrich options with searchTerms for compatibility (include all language variants)
      const enrichedOptions = options.map(opt => ({
        ...opt,
        mainProfessionId: opt.professionId,
        searchTerms: [opt.label, opt.label_en, opt.label_fr, opt.label_ru].filter(Boolean) as string[]
      }));
      // Filter out "all" option
      const filteredOptions = enrichedOptions.filter(opt => opt.id !== 'all');
      setProfessionOptions(filteredOptions);
    };
    loadProfessions();
  }, []);

  // Build display options with translated labels (using static DB translations)
  const translatedProfessions = useMemo(() => {
    return professionOptions.map(opt => ({
      ...opt,
      label: getLabel(opt, currentLang)
    }));
  }, [professionOptions, currentLang]);

  const translatedLocations = useMemo(() => {
    return locationOptions.map(opt => ({
      ...opt,
      label: getLabel(opt, currentLang)
    }));
  }, [locationOptions, currentLang]);

  // Filter options based on search text
  const debouncedProfessionSearch = useDebounce(searchText.profession, 300);
  const debouncedLocationSearch = useDebounce(searchText.location, 300);

  const filteredProfessions = translatedProfessions.filter(p => {
    if (!debouncedProfessionSearch) return true;

    // Fuzzy search in label
    if (fuzzyMatch(debouncedProfessionSearch, p.label)) return true;

    // Fuzzy search in searchTerms (includes all language variants)
    if (p.searchTerms?.some(term => fuzzyMatch(debouncedProfessionSearch, term))) return true;

    // Exact ID match as fallback
    if (p.id.toLowerCase().includes(debouncedProfessionSearch.toLowerCase())) return true;

    return false;
  });

  const filteredLocations = translatedLocations.filter(l =>
    !debouncedLocationSearch ||
    l.label.toLowerCase().includes(debouncedLocationSearch.toLowerCase()) ||
    l.id.toLowerCase().includes(debouncedLocationSearch.toLowerCase())
  );

  // Only update if props change and are different from current state
  useEffect(() => {
    if (initialProfession && initialProfession !== profession) {
      setProfession(initialProfession);
    }
  }, [initialProfession]);

  useEffect(() => {
    if (initialLocation && initialLocation !== location) {
      setLocation(initialLocation);
    }
  }, [initialLocation]);

  useEffect(() => {
    // Close dropdowns when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen({ profession: false, location: false });
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfessionSelect = (id: string, label: string) => {
    setProfession(id);
    setProfessionError(null); // Clear error when profession is selected
    setIsOpen({ ...isOpen, profession: false });
    setSearchText({ ...searchText, profession: '' });
    // Perform search automatically when both fields are selected
    if (location !== 'all') {
      onSearch(id, location, label);
    }
  };

  const handleLocationSelect = (id: string) => {
    setLocation(id);
    setIsOpen({ ...isOpen, location: false });
    setSearchText({ ...searchText, location: '' });
    // Perform search automatically when both fields are selected
    if (profession !== 'all') {
      const profLabel = translatedProfessions.find(p => p.id === profession)?.label;
      onSearch(profession, id, profLabel);
    }
  };

  const handleSearch = () => {
    // Validate profession selection
    if (profession === 'all' || !profession) {
      setProfessionError(t('search.professionRequired'));
      return; // Don't proceed with search
    }
    
    // Clear any previous error
    setProfessionError(null);
    const profLabel = translatedProfessions.find(p => p.id === profession)?.label;
    onSearch(profession, location, profLabel);
  };

  const handleProfessionSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText({ ...searchText, profession: e.target.value });
  };

  const handleLocationSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText({ ...searchText, location: e.target.value });
  };

  // Find the display labels for the current profession and location (using translated)
  const currentProfessionLabel = profession === 'all' || !profession
    ? t('search.selectProfession')
    : (translatedProfessions.find(p => p.id === profession)?.label || t('search.selectProfession'));

  const currentLocationLabel = location === 'all'
    ? t('search.allCountry')
    : (translatedLocations.find(l => l.id === location)?.label || t('search.selectArea'));

  return (
    <div className="glass-card p-4 md:p-6 rounded-xl shadow-lg" ref={dropdownRef}>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <div 
            className="border rounded-lg p-3 flex items-center justify-between cursor-pointer bg-white"
            onClick={() => setIsOpen({ 
              profession: !isOpen.profession, 
              location: false 
            })}
          >
            <span className="text-gray-700">
              {currentProfessionLabel}
            </span>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isOpen.profession && (
            <div className="absolute z-[50] mt-1 w-full rounded-md bg-white shadow-lg max-h-60 overflow-auto">
              <div className="sticky top-0 p-2 bg-white border-b">
                <input
                  type="text"
                  placeholder={t('search.searchProfessionPlaceholder')}
                  className="w-full p-2 border rounded-md text-right"
                  value={searchText.profession}
                  onChange={handleProfessionSearchChange}
                  autoFocus
                />
              </div>
              <ul className="py-1">
                {filteredProfessions.map(item => (
                  <li 
                    key={item.id} 
                    className="cursor-pointer px-4 py-2 hover:bg-blue-50"
                    onClick={() => handleProfessionSelect(item.id, item.label)}
                  >
                    {item.label}
                  </li>
                ))}
                {filteredProfessions.length === 0 && (
                  <li className="px-4 py-2 text-gray-500">{t('search.noResults')}</li>
                )}
              </ul>
            </div>
          )}
        </div>
        
        <div className="relative flex-1">
          <div 
            className="border rounded-lg p-3 flex items-center justify-between cursor-pointer bg-white"
            onClick={() => setIsOpen({ 
              profession: false,
              location: !isOpen.location 
            })}
          >
            <span className="flex items-center text-gray-700">
              <MapPin size={16} className="mr-2 text-blue-500" />
              {currentLocationLabel}
            </span>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isOpen.location && (
            <div className="absolute z-[50] mt-1 w-full rounded-md bg-white shadow-lg max-h-60 overflow-auto">
              <div className="sticky top-0 p-2 bg-white border-b">
                <input
                  type="text"
                  placeholder={t('search.searchAreaPlaceholder')}
                  className="w-full p-2 border rounded-md text-right"
                  value={searchText.location}
                  onChange={handleLocationSearchChange}
                  autoFocus
                />
              </div>
              <ul className="py-1">
                {filteredLocations.map(item => (
                  <li 
                    key={item.id} 
                    className="cursor-pointer px-4 py-2 hover:bg-blue-50"
                    onClick={() => handleLocationSelect(item.id)}
                  >
                    {item.label}
                  </li>
                ))}
                {filteredLocations.length === 0 && (
                  <li className="px-4 py-2 text-gray-500">{t('search.noResults')}</li>
                )}
              </ul>
            </div>
          )}
        </div>
        
        <div className="flex flex-col items-center">
          <Button
            className="bg-[#00D09E] hover:bg-[#00C090] flex items-center gap-2 px-6"
            onClick={handleSearch}
          >
            <Search size={18} />
            <span>{t('common.search')}</span>
          </Button>
          {professionError && (
            <p className="text-red-500 text-sm mt-2 text-center">
              {professionError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
